import MarketingLayout from "@/components/MarketingLayout";
import { FileText } from "lucide-react";
import { Link } from "wouter";

export default function TermsOfService() {
  return (
    <MarketingLayout>
      <section className="container py-16">
        <div className="max-w-3xl mx-auto space-y-8">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6" /> Terms of Service
            </h1>
            <p className="text-muted-foreground mt-1">
              Last updated: 8 March 2026
            </p>
          </div>

          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-sm leading-relaxed text-foreground">
            <section className="space-y-2">
              <h2 className="text-lg font-semibold">1. Agreement</h2>
              <p className="text-muted-foreground">
                These Terms of Service ("Terms") govern your access to and use of
                the Double O platform, website (<strong>oovault.nl</strong>), API,
                and related services (collectively, the "Service") operated by
                Double O ("we", "us", "our"). By creating an account or using the
                Service, you agree to be bound by these Terms.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">2. Eligibility</h2>
              <p className="text-muted-foreground">
                You must be at least 16 years old and have the legal capacity to
                enter into a binding agreement. By using the Service you represent
                that you meet these requirements.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">3. Account Responsibilities</h2>
              <p className="text-muted-foreground">
                You are responsible for maintaining the confidentiality of your
                account credentials, including any API tokens generated through the
                Service. You agree to notify us immediately of any unauthorised use.
                We are not liable for losses arising from unauthorised access to
                your account.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">4. Acceptable Use</h2>
              <p className="text-muted-foreground">You agree not to:</p>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li>Use the Service for any unlawful purpose or in violation of applicable laws.</li>
                <li>Attempt to gain unauthorised access to systems, accounts, or data.</li>
                <li>Interfere with or disrupt the integrity or performance of the Service.</li>
                <li>Reverse-engineer, decompile, or disassemble any part of the Service.</li>
                <li>Use the Service to store content that infringes third-party rights.</li>
                <li>Exceed published rate limits or abuse API access.</li>
                <li>Resell, redistribute, or sublicence the Service without prior written consent.</li>
              </ul>
              <p className="text-muted-foreground">
                Violation of these rules may result in immediate suspension or
                termination of your account.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">5. Your Content</h2>
              <p className="text-muted-foreground">
                You retain all rights to the secrets, credentials, and other data
                you store through the Service ("Your Content"). By using the
                Service, you grant us a limited licence to host, transmit, and
                process Your Content solely to provide and improve the Service. We
                will never access or disclose Your Content except as required to
                operate the Service, comply with law, or with your explicit
                permission.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">6. Subscription and Billing</h2>
              <p className="text-muted-foreground">
                Paid plans are billed in advance on a monthly basis via Stripe.
                Prices are listed in Euros and are exclusive of applicable taxes
                unless stated otherwise. We reserve the right to change pricing with
                30 days' notice. Refunds are handled on a case-by-case basis at our
                discretion.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">7. Service Availability</h2>
              <p className="text-muted-foreground">
                We strive to maintain high availability but do not guarantee
                uninterrupted or error-free operation. The Service is provided on an
                "as is" and "as available" basis. We may perform scheduled
                maintenance with reasonable notice where possible.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">8. Limitation of Liability</h2>
              <p className="text-muted-foreground">
                To the maximum extent permitted by applicable law:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li>
                  We shall not be liable for any indirect, incidental, special,
                  consequential, or punitive damages, including loss of profits,
                  data, or business opportunities.
                </li>
                <li>
                  Our total aggregate liability for any claims arising from or
                  related to the Service shall not exceed the amount you paid us in
                  the twelve (12) months preceding the claim.
                </li>
                <li>
                  We are not responsible for the security of third-party services
                  you integrate with, or for secrets exposed due to your own
                  misconfiguration or negligence.
                </li>
              </ul>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">9. Indemnification</h2>
              <p className="text-muted-foreground">
                You agree to indemnify and hold harmless Double O, its officers,
                employees, and affiliates from any claims, damages, or expenses
                (including reasonable legal fees) arising from your use of the
                Service, your violation of these Terms, or your violation of any
                third-party rights.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">10. Intellectual Property</h2>
              <p className="text-muted-foreground">
                The Service, its original content (excluding Your Content),
                features, and functionality are and will remain the exclusive
                property of Double O. Our trademarks and trade dress may not be used
                without prior written consent.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">11. Termination</h2>
              <p className="text-muted-foreground">
                You may close your account at any time. We may suspend or terminate
                access to the Service immediately, without prior notice, for conduct
                that we believe violates these Terms or is harmful to other users or
                us. Upon termination, your right to use the Service ceases
                immediately. Provisions that by their nature should survive
                (including liability limits, indemnification, and governing law)
                will survive termination.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">12. Disclaimer of Warranties</h2>
              <p className="text-muted-foreground">
                The Service is provided "as is" without warranties of any kind,
                whether express or implied, including but not limited to implied
                warranties of merchantability, fitness for a particular purpose, and
                non-infringement. We do not warrant that the Service will meet your
                specific requirements or that operation will be uninterrupted or
                error-free.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">13. Governing Law</h2>
              <p className="text-muted-foreground">
                These Terms shall be governed by and construed in accordance with
                the laws of the Netherlands, without regard to conflict-of-law
                principles. Any disputes shall be submitted to the competent courts
                in the Netherlands. Nothing in these Terms affects your statutory
                consumer rights under applicable law.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">14. Changes to These Terms</h2>
              <p className="text-muted-foreground">
                We reserve the right to modify these Terms at any time. Material
                changes will be communicated at least 30 days before taking effect
                via email or a notice on the website. Continued use of the Service
                after changes take effect constitutes acceptance.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">15. Privacy</h2>
              <p className="text-muted-foreground">
                Your use of the Service is also governed by our{" "}
                <Link to="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </Link>
                , which describes how we collect, use, and protect your personal data.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">16. Contact</h2>
              <p className="text-muted-foreground">
                For questions about these Terms, contact us at{" "}
                <a href="mailto:legal@oovault.nl" className="text-primary hover:underline">
                  legal@oovault.nl
                </a>.
              </p>
            </section>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
